import React, { useState, useEffect } from 'react';
import { Calendar } from './ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { lifeAPI } from '../services/api';
import { useLocale } from '../context/LocaleContext';
import { DollarSign, Calendar as CalendarIcon, FileText, Receipt } from 'lucide-react';
import { format } from 'date-fns';
import { DayPicker } from 'react-day-picker';

const LifeCalendar = () => {
  const { t } = useLocale();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [expenseData, setExpenseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [monthlyData, setMonthlyData] = useState({});

  useEffect(() => {
    if (selectedDate) {
      loadMonthlyData(selectedDate.getFullYear(), selectedDate.getMonth() + 1);
    }
  }, [selectedDate]);

  const loadMonthlyData = async (year, month) => {
    try {
      const response = await lifeAPI.getMonthlyCalendar(year, month);
      setMonthlyData(response.data);
    } catch (error) {
      console.error('Error loading monthly data:', error);
    }
  };

  const loadExpensesForDate = async (date) => {
    setLoading(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await lifeAPI.getExpensesByDate(dateStr);
      setExpenseData(response.data);
      setShowDetailsDialog(true);
    } catch (error) {
      console.error('Error loading expense data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    if (!date) return;
    setSelectedDate(date);
    loadExpensesForDate(date);
  };

  const handleMonthChange = (month) => {
    setSelectedDate(month);
    loadMonthlyData(month.getFullYear(), month.getMonth() + 1);
  };

  // Create arrays of dates for each modifier
  const getDatesWithExpenses = () => {
    if (!monthlyData.data_by_date) return [];
    const dates = [];
    Object.entries(monthlyData.data_by_date).forEach(([dateStr, data]) => {
      if (data.expenses > 0) {
        // Fix timezone issue: parse date parts manually to avoid UTC interpretation
        const [year, month, day] = dateStr.split('-').map(Number);
        dates.push(new Date(year, month - 1, day)); // month is 0-indexed
      }
    });
    return dates;
  };

  const getDatesWithAppointments = () => {
    if (!monthlyData.data_by_date) return [];
    const dates = [];
    Object.entries(monthlyData.data_by_date).forEach(([dateStr, data]) => {
      if (data.appointments > 0) {
        // Fix timezone issue: parse date parts manually to avoid UTC interpretation
        const [year, month, day] = dateStr.split('-').map(Number);
        dates.push(new Date(year, month - 1, day)); // month is 0-indexed
      }
    });
    return dates;
  };

  const getDatesWithBills = () => {
    if (!monthlyData.data_by_date) return [];
    const dates = [];
    Object.entries(monthlyData.data_by_date).forEach(([dateStr, data]) => {
      if (data.bills > 0) {
        // Fix timezone issue: parse date parts manually to avoid UTC interpretation
        const [year, month, day] = dateStr.split('-').map(Number);
        dates.push(new Date(year, month - 1, day)); // month is 0-indexed
      }
    });
    return dates;
  };

  const modifiers = {
    hasExpenses: getDatesWithExpenses(),
    hasAppointments: getDatesWithAppointments(),
    hasBills: getDatesWithBills()
  };

  const modifiersStyles = {
    hasExpenses: { fontWeight: 'bold', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: '0.375rem' },
    hasAppointments: { fontWeight: 'bold', backgroundColor: '#dbeafe', color: '#1e40af', borderRadius: '0.375rem' },
    hasBills: { fontWeight: 'bold', backgroundColor: '#fef3c7', color: '#92400e', borderRadius: '0.375rem' }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Food': 'bg-orange-100 text-orange-800',
      'Transportation': 'bg-blue-100 text-blue-800',
      'Entertainment': 'bg-teal-100 text-teal-800',
      'Shopping': 'bg-cyan-100 text-pink-800',
      'Healthcare': 'bg-red-100 text-red-800',
      'Bills': 'bg-yellow-100 text-yellow-800',
      'Other': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors['Other'];
  };

  // Custom day content renderer with indicator dots
  const renderDayContent = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    const dayData = monthlyData.data_by_date?.[dateStr];
    
    return (
      <div className="relative w-full h-full flex flex-col items-center justify-center">
        <span>{format(day, 'd')}</span>
        {dayData && (
          <div className="flex gap-0.5 mt-0.5">
            {dayData.expenses > 0 && (
              <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>
            )}
            {dayData.appointments > 0 && (
              <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
            )}
            {dayData.bills > 0 && (
              <div className="w-1.5 h-1.5 rounded-full bg-yellow-500"></div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <style>{`
        /* Enhanced Life Calendar Styling */
        .life-calendar-enhanced .rdp {
          --rdp-cell-size: 52px;
          --rdp-accent-color: #8b5cf6;
          --rdp-background-color: #ede9fe;
        }
        
        .life-calendar-enhanced .rdp-day {
          border-radius: 0.5rem;
          transition: all 0.2s ease;
          position: relative;
        }
        
        .life-calendar-enhanced .rdp-day:hover {
          transform: scale(1.08);
          box-shadow: 0 4px 15px rgba(139, 92, 246, 0.3);
          z-index: 10;
        }
        
        .life-calendar-enhanced .has-expenses-indicator {
          position: relative;
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          border: 2px solid #ef4444;
          font-weight: 600;
        }
        
        .life-calendar-enhanced .has-expenses-indicator::after {
          content: '💰';
          position: absolute;
          bottom: -4px;
          left: 2px;
          font-size: 11px;
        }
        
        .life-calendar-enhanced .has-appointments-indicator {
          position: relative;
          background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
          border: 2px solid #3b82f6;
          font-weight: 600;
        }
        
        .life-calendar-enhanced .has-appointments-indicator::before {
          content: '📅';
          position: absolute;
          top: -4px;
          right: 2px;
          font-size: 11px;
        }
        
        .life-calendar-enhanced .has-bills-indicator {
          position: relative;
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          border: 2px solid #eab308;
          font-weight: 600;
        }
        
        .life-calendar-enhanced .has-bills-indicator::after {
          content: '📄';
          position: absolute;
          bottom: -4px;
          right: 2px;
          font-size: 11px;
        }
        
        /* Multiple indicators on same date */
        .life-calendar-enhanced .has-expenses-indicator.has-appointments-indicator {
          background: linear-gradient(135deg, #fee2e2 0%, #dbeafe 100%);
          border: 2px solid #4DC9C4;
        }
        
        .life-calendar-enhanced .rdp-day_selected {
          background: linear-gradient(135deg, #8b5cf6 0%, #239490 100%);
          color: white;
          font-weight: bold;
          box-shadow: 0 4px 15px rgba(139, 92, 246, 0.6);
          transform: scale(1.1);
        }
        
        .life-calendar-enhanced .rdp-head_cell {
          color: #239490;
          font-weight: 700;
          font-size: 0.875rem;
          text-transform: uppercase;
        }
        
        .life-calendar-enhanced .rdp-caption {
          color: #239490;
          font-weight: 700;
          font-size: 1.125rem;
        }
      `}</style>
      
      {/* Enhanced Monthly Summary with Gradients */}
      {monthlyData.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-2 border-red-200 bg-gradient-to-br from-red-50 to-cyan-50 hover:shadow-xl transition-all duration-300 cursor-pointer group">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-red-100 rounded-xl group-hover:bg-red-200 transition-colors">
                  <DollarSign className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <p className="text-xs text-red-600 font-semibold uppercase tracking-wide">{t('life.totalExpenses')}</p>
                  <p className="text-2xl font-bold text-red-900">${monthlyData.summary.total_expenses.toFixed(2)}</p>
                  <p className="text-xs text-red-500 mt-1">💰 {t('life.monthlySpend')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50 hover:shadow-xl transition-all duration-300 cursor-pointer group">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-blue-100 rounded-xl group-hover:bg-blue-200 transition-colors">
                  <Receipt className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide">{t('life.transactions')}</p>
                  <p className="text-2xl font-bold text-blue-900">{monthlyData.summary.total_transactions}</p>
                  <p className="text-xs text-blue-500 mt-1">📊 {t('life.totalCount')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-violet-50 hover:shadow-xl transition-all duration-300 cursor-pointer group">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-teal-100 rounded-xl group-hover:bg-teal-200 transition-colors">
                  <CalendarIcon className="h-6 w-6 text-teal-600" />
                </div>
                <div>
                  <p className="text-xs text-teal-600 font-semibold uppercase tracking-wide">{t('life.appointmentsCount')}</p>
                  <p className="text-2xl font-bold text-teal-900">{monthlyData.summary.total_appointments}</p>
                  <p className="text-xs text-teal-500 mt-1">📅 {t('life.scheduled')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-yellow-200 bg-gradient-to-br from-yellow-50 to-amber-50 hover:shadow-xl transition-all duration-300 cursor-pointer group">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-yellow-100 rounded-xl group-hover:bg-yellow-200 transition-colors">
                  <FileText className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-xs text-yellow-600 font-semibold uppercase tracking-wide">{t('life.billsDue')}</p>
                  <p className="text-2xl font-bold text-yellow-900">{monthlyData.summary.total_bills}</p>
                  <p className="text-xs text-yellow-500 mt-1">📄 {t('life.pending')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Enhanced Calendar with Visual Indicators */}
      <Card className="border-2 border-teal-200 bg-gradient-to-br from-teal-50 via-violet-50 to-fuchsia-50 shadow-xl">
        <CardHeader className="border-b border-teal-100 bg-white/50 backdrop-blur">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <CalendarIcon className="h-6 w-6 text-teal-600" />
            </div>
            <div>
              <CardTitle className="text-2xl bg-gradient-to-r from-teal-600 to-violet-600 bg-clip-text text-transparent">
                💰 {t('life.lifeCalendar')}
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                {t('life.trackExpensesAppointments')} • <span className="font-semibold text-teal-600">{t('life.visualIndicators')}</span>
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex justify-center w-full overflow-x-auto p-4">
            <div className="life-calendar-enhanced">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                onMonthChange={handleMonthChange}
                modifiers={modifiers}
                modifiersStyles={modifiersStyles}
                modifiersClassNames={{
                  hasExpenses: 'has-expenses-indicator',
                  hasAppointments: 'has-appointments-indicator',
                  hasBills: 'has-bills-indicator'
                }}
                className="rounded-xl border-2 border-teal-200 bg-white shadow-lg p-4"
              />
            </div>
          </div>
          
          {/* Enhanced Legend with Icons */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="flex items-center gap-3 bg-gradient-to-r from-red-50 to-cyan-50 px-4 py-3 rounded-xl border-2 border-red-200 hover:shadow-md transition-shadow">
              <div className="text-2xl">💰</div>
              <div>
                <p className="font-semibold text-red-700">{t('life.expenses')}</p>
                <p className="text-xs text-red-600">{t('life.expensesMoneySpent')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-gradient-to-r from-blue-50 to-cyan-50 px-4 py-3 rounded-xl border-2 border-blue-200 hover:shadow-md transition-shadow">
              <div className="text-2xl">📅</div>
              <div>
                <p className="font-semibold text-blue-700">{t('life.appointments')}</p>
                <p className="text-xs text-blue-600">{t('life.appointmentsScheduled')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-gradient-to-r from-yellow-50 to-amber-50 px-4 py-3 rounded-xl border-2 border-yellow-200 hover:shadow-md transition-shadow">
              <div className="text-2xl">📄</div>
              <div>
                <p className="font-semibold text-yellow-700">{t('life.billsDue')}</p>
                <p className="text-xs text-yellow-600">{t('life.billsPaymentReminders')}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calendar */}
      <div className="hidden">
        <Card>
          <CardHeader>
            <CardTitle>{t('life.lifeCalendar')}</CardTitle>
            <p className="text-sm text-gray-500">
              {t('life.clickDateToView')}
            </p>
            <div className="flex gap-4 mt-2 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-red-100"></div>
                <span>{t('life.expenses')}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-blue-100"></div>
                <span>{t('life.appointments')}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-yellow-100"></div>
                <span>{t('life.bills')}</span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center w-full overflow-x-auto">
              <div className="calendar-with-indicators">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  onMonthChange={handleMonthChange}
                  modifiers={modifiers}
                  modifiersStyles={modifiersStyles}
                  modifiersClassNames={{
                    hasExpenses: 'has-expenses-indicator',
                    hasAppointments: 'has-appointments-indicator',
                    hasBills: 'has-bills-indicator'
                  }}
                  className="rounded-md border mx-auto"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t('life.expensesFor')} {selectedDate && format(selectedDate, 'MMMM dd, yyyy')}
            </DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : expenseData ? (
            <div className="space-y-6">
              {/* Summary Card */}
              <Card className="bg-gradient-to-r from-blue-50 to-teal-50">
                <CardContent className="pt-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-500">{t('life.totalSpent')}</p>
                      <p className="text-3xl font-bold text-blue-600">
                        ${expenseData.total.toFixed(2)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">{t('life.transactions')}</p>
                      <p className="text-3xl font-bold text-teal-600">
                        {expenseData.count}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Category Breakdown */}
              {expenseData.categories && Object.keys(expenseData.categories).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{t('life.categoryBreakdown')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {Object.entries(expenseData.categories).map(([category, amount]) => (
                        <div key={category} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getCategoryColor(category)}`}>
                              {category}
                            </span>
                          </div>
                          <p className="font-bold text-lg">${amount.toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Expense List */}
              {expenseData.expenses && expenseData.expenses.length > 0 ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{t('life.allTransactions')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {expenseData.expenses.map((expense) => (
                        <div key={expense.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                          <div className="flex-1">
                            <p className="font-medium">{expense.description}</p>
                            <div className="flex gap-2 mt-1">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getCategoryColor(expense.category)}`}>
                                {expense.category}
                              </span>
                              {expense.notes && (
                                <span className="text-xs text-gray-500 italic">
                                  {expense.notes}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right ml-4">
                            <p className="font-bold text-xl text-red-600">
                              ${expense.amount.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="pt-6 text-center text-gray-500">
                    <DollarSign className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>{t('life.noExpensesRecorded')}</p>
                    <p className="text-sm mt-1">{t('life.startTrackingSpending')}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LifeCalendar;
